/**
 * Survey Configuration Page - YUL ON TAP
 * 
 * Phase 3.5: Admin-controlled survey question management.
 * - Edit question text (EN/FR)
 * - Enable/disable questions
 * - Reorder questions (up/down)
 * - Response type selection
 * 
 * Constraints: Min 2, Max 6 questions.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { surveyConfigAPI, hotelsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import LanguageSelector from '../../components/LanguageSelector';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ClipboardCheck,
  Star,
  Eye,
  Save,
  GripVertical,
  Building2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

export default function SurveyConfiguration() {
  const { t, i18n } = useTranslation();
  const { id: hotelId } = useParams();
  const navigate = useNavigate();
  
  const [hotel, setHotel] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(null);
  
  // Form state
  const [newQuestion, setNewQuestion] = useState({
    question_text_en: '',
    question_text_fr: '',
    response_type: 'rating',
  });

  useEffect(() => {
    fetchData();
  }, [hotelId]);

  const fetchData = async () => {
    try {
      const [hotelRes, configRes] = await Promise.all([
        hotelsAPI.get(hotelId),
        surveyConfigAPI.getConfig(hotelId),
      ]);
      setHotel(hotelRes.data);
      setQuestions(configRes.data.questions || []);
    } catch (error) {
      toast.error(t('hotels.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await surveyConfigAPI.updateConfig(hotelId, questions);
      toast.success(t('surveyConfig.messages.saveSuccess'));
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = async () => {
    if (questions.length >= 6) {
      toast.error(t('surveyConfig.messages.maxError'));
      return;
    }
    
    try {
      const response = await surveyConfigAPI.addQuestion(hotelId, newQuestion);
      setQuestions([...questions, response.data.question]);
      toast.success(t('surveyConfig.messages.addSuccess'));
      setShowAddModal(false);
      setNewQuestion({ question_text_en: '', question_text_fr: '', response_type: 'rating' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;
    
    try {
      await surveyConfigAPI.updateQuestion(hotelId, editingQuestion.id, {
        question_text_en: editingQuestion.question_text_en,
        question_text_fr: editingQuestion.question_text_fr,
        response_type: editingQuestion.response_type,
        enabled: editingQuestion.enabled,
      });
      
      setQuestions(questions.map(q => 
        q.id === editingQuestion.id ? editingQuestion : q
      ));
      toast.success(t('surveyConfig.messages.updateSuccess'));
      setShowEditModal(false);
      setEditingQuestion(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleDeleteQuestion = async () => {
    if (!deletingQuestion) return;
    
    try {
      await surveyConfigAPI.deleteQuestion(hotelId, deletingQuestion.id);
      setQuestions(questions.filter(q => q.id !== deletingQuestion.id));
      toast.success(t('surveyConfig.messages.deleteSuccess'));
      setShowDeleteDialog(false);
      setDeletingQuestion(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleToggleEnabled = async (questionId, enabled) => {
    const enabledCount = questions.filter(q => q.enabled && q.id !== questionId).length + (enabled ? 1 : 0);
    if (enabledCount < 2) {
      toast.error(t('surveyConfig.messages.minError'));
      return;
    }
    
    try {
      await surveyConfigAPI.updateQuestion(hotelId, questionId, { enabled });
      setQuestions(questions.map(q => 
        q.id === questionId ? { ...q, enabled } : q
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newQuestions = [...questions];
    [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
    newQuestions.forEach((q, i) => q.order = i + 1);
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  const handleMoveDown = (index) => {
    if (index === questions.length - 1) return;
    const newQuestions = [...questions];
    [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
    newQuestions.forEach((q, i) => q.order = i + 1);
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  const enabledQuestions = questions.filter(q => q.enabled);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(224,71%,4%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)]">
      {/* Header */}
      <header className="border-b border-[hsl(217,19%,27%)] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/hotels/${hotelId}/engagement`)}
              data-testid="back-btn"
              className="flex items-center space-x-2 text-[hsl(215,20%,65%)] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('engagement.title')}</span>
            </button>
            <div className="h-6 w-px bg-[hsl(217,19%,27%)]" />
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <span className="text-lg font-semibold text-white">{t('surveyConfig.title')}</span>
                <p className="text-xs text-[hsl(215,20%,65%)]">{hotel?.name}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <LanguageSelector variant="compact" />
            
            <Button
              variant="outline"
              data-testid="preview-btn"
              onClick={() => setShowPreview(true)}
              className="border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,17%)]"
            >
              <Eye className="w-4 h-4 mr-2" />
              {t('surveyConfig.preview')}
            </Button>
            
            {hasChanges && (
              <Button
                data-testid="save-all-btn"
                onClick={handleSaveAll}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t('common.save')}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        <div className="flex items-center space-x-4 text-sm text-[hsl(215,20%,65%)]">
          <span>{t('surveyConfig.questionsCount', { count: questions.length })}</span>
          <span>•</span>
          <span>{t('surveyConfig.enabledCount', { count: enabledQuestions.length })}</span>
          <span>•</span>
          <span className="text-yellow-500">{t('surveyConfig.minQuestions')}</span>
        </div>

        {/* Questions List */}
        <div className="space-y-3">
          {questions.sort((a, b) => a.order - b.order).map((question, index) => (
            <Card
              key={question.id}
              data-testid={`question-card-${index}`}
              className={`bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] ${
                !question.enabled ? 'opacity-60' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start space-x-4">
                  {/* Drag Handle & Order */}
                  <div className="flex flex-col items-center space-y-1 pt-1">
                    <GripVertical className="w-4 h-4 text-[hsl(215,20%,45%)]" />
                    <span className="text-xs text-[hsl(215,20%,65%)] font-medium">Q{index + 1}</span>
                  </div>
                  
                  {/* Question Content */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-white font-medium">
                          {i18n.language === 'fr' ? question.question_text_fr : question.question_text_en}
                        </p>
                        <div className="flex items-center space-x-3 text-xs">
                          <span className={`px-2 py-0.5 rounded-full ${
                            question.response_type === 'rating' 
                              ? 'bg-amber-500/20 text-amber-400' 
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {question.response_type === 'rating' ? t('surveyConfig.rating') : t('surveyConfig.yesNoNeutral')}
                          </span>
                          {question.enabled ? (
                            <span className="text-green-400">{t('surveyConfig.enabled')}</span>
                          ) : (
                            <span className="text-red-400">{t('surveyConfig.disabled')}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-2">
                        <Switch
                          data-testid={`toggle-q-${index}`}
                          checked={question.enabled}
                          onCheckedChange={(checked) => handleToggleEnabled(question.id, checked)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className="text-[hsl(215,20%,65%)] hover:text-white"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === questions.length - 1}
                          className="text-[hsl(215,20%,65%)] hover:text-white"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-q-${index}`}
                          onClick={() => {
                            setEditingQuestion({ ...question });
                            setShowEditModal(true);
                          }}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-q-${index}`}
                          onClick={() => {
                            setDeletingQuestion(question);
                            setShowDeleteDialog(true);
                          }}
                          disabled={questions.length <= 2}
                          className="text-red-400 hover:text-red-300 disabled:opacity-30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Show both languages */}
                    <div className="text-xs text-[hsl(215,20%,45%)] pt-2 border-t border-[hsl(217,19%,27%)]">
                      <p><span className="text-[hsl(215,20%,65%)]">EN:</span> {question.question_text_en}</p>
                      <p><span className="text-[hsl(215,20%,65%)]">FR:</span> {question.question_text_fr}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add Question Button */}
        {questions.length < 6 && (
          <Button
            data-testid="add-question-btn"
            onClick={() => setShowAddModal(true)}
            variant="outline"
            className="w-full border-dashed border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:text-white hover:bg-[hsl(217,19%,17%)]"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('surveyConfig.addQuestion')}
          </Button>
        )}

        {/* Max reached notice */}
        {questions.length >= 6 && (
          <p className="text-center text-sm text-yellow-500">{t('surveyConfig.maxQuestions')}</p>
        )}
      </main>

      {/* Add Question Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle>{t('surveyConfig.addQuestion')}</DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,65%)]">
              {t('surveyConfig.subtitle')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('surveyConfig.questionTextEn')}</Label>
              <Textarea
                data-testid="new-q-en"
                value={newQuestion.question_text_en}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_text_en: e.target.value })}
                placeholder="Enter question in English..."
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('surveyConfig.questionTextFr')}</Label>
              <Textarea
                data-testid="new-q-fr"
                value={newQuestion.question_text_fr}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_text_fr: e.target.value })}
                placeholder="Entrez la question en français..."
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('surveyConfig.responseType')}</Label>
              <Select
                value={newQuestion.response_type}
                onValueChange={(v) => setNewQuestion({ ...newQuestion, response_type: v })}
              >
                <SelectTrigger data-testid="new-q-type" className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
                  <SelectItem value="rating" className="text-white">{t('surveyConfig.rating')}</SelectItem>
                  <SelectItem value="yes_no_neutral" className="text-white">{t('surveyConfig.yesNoNeutral')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)} className="text-[hsl(215,20%,65%)]">
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="confirm-add-btn"
              onClick={handleAddQuestion}
              disabled={!newQuestion.question_text_en || !newQuestion.question_text_fr}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {t('surveyConfig.addQuestion')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white">
          <DialogHeader>
            <DialogTitle>{t('surveyConfig.editQuestion')}</DialogTitle>
          </DialogHeader>
          
          {editingQuestion && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('surveyConfig.questionTextEn')}</Label>
                <Textarea
                  data-testid="edit-q-en"
                  value={editingQuestion.question_text_en}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question_text_en: e.target.value })}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('surveyConfig.questionTextFr')}</Label>
                <Textarea
                  data-testid="edit-q-fr"
                  value={editingQuestion.question_text_fr}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question_text_fr: e.target.value })}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('surveyConfig.responseType')}</Label>
                <Select
                  value={editingQuestion.response_type}
                  onValueChange={(v) => setEditingQuestion({ ...editingQuestion, response_type: v })}
                >
                  <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
                    <SelectItem value="rating" className="text-white">{t('surveyConfig.rating')}</SelectItem>
                    <SelectItem value="yes_no_neutral" className="text-white">{t('surveyConfig.yesNoNeutral')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)} className="text-[hsl(215,20%,65%)]">
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="confirm-edit-btn"
              onClick={handleUpdateQuestion}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('surveyConfig.deleteQuestion')}</AlertDialogTitle>
            <AlertDialogDescription className="text-[hsl(215,20%,65%)]">
              {deletingQuestion?.question_text_en}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,17%)]">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDeleteQuestion}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900">{t('surveyConfig.livePreview')}</DialogTitle>
            <DialogDescription>
              {hotel?.name} - {t('engagement.types.inRoomSurvey')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {enabledQuestions.sort((a, b) => a.order - b.order).map((q, i) => (
              <div key={q.id} className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  {i + 1}. {i18n.language === 'fr' ? q.question_text_fr : q.question_text_en}
                </p>
                
                {q.response_type === 'rating' ? (
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-6 h-6 text-gray-300" />
                    ))}
                  </div>
                ) : (
                  <div className="flex space-x-2 text-xs">
                    <span className="px-3 py-1 bg-gray-100 rounded">{t('common.yes')}</span>
                    <span className="px-3 py-1 bg-gray-100 rounded">{t('common.no')}</span>
                    <span className="px-3 py-1 bg-gray-100 rounded">Partially</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPreview(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
